/**
 * Password Hashing Utilities
 * 
 * Uses PBKDF2 with SHA-256 for secure password hashing.
 * - Unique random salt per password
 * - 100,000 iterations (adjustable for security/performance tradeoff)
 * - Works in both browser and Node.js via Web Crypto API
 */

// Number of PBKDF2 iterations - higher = more secure but slower
// 100,000 is OWASP recommended minimum as of 2023
const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 16; // 128 bits
const KEY_LENGTH = 32; // 256 bits

/**
 * Generate a cryptographically secure random salt
 */
export function generateSalt(): string {
  const saltBytes = new Uint8Array(SALT_LENGTH);
  crypto.getRandomValues(saltBytes);
  return arrayToHex(saltBytes);
}

/**
 * Hash a password using PBKDF2-SHA256
 * @param password - The plain text password
 * @param salt - The salt (hex string) - generate with generateSalt() for new passwords
 * @returns The hashed password as a hex string
 */
export async function hashPassword(password: string, salt: string): Promise<string> {
  const encoder = new TextEncoder();
  const passwordData = encoder.encode(password);
  const saltData = hexToArray(salt);
  
  // Import the password as a key
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    passwordData,
    'PBKDF2',
    false,
    ['deriveBits']
  );
  
  // Derive bits using PBKDF2
  // Create a fresh ArrayBuffer from the Uint8Array to satisfy TypeScript
  const saltBuffer = new ArrayBuffer(saltData.length);
  new Uint8Array(saltBuffer).set(saltData);
  
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    passwordKey,
    KEY_LENGTH * 8 // bits
  );
  
  return arrayToHex(new Uint8Array(derivedBits));
}

/**
 * Verify a password against a stored hash
 * @param password - The plain text password to verify
 * @param salt - The salt used when hashing (hex string)
 * @param storedHash - The stored password hash (hex string)
 * @returns true if the password matches
 */
export async function verifyPassword(
  password: string,
  salt: string,
  storedHash: string
): Promise<boolean> {
  const computedHash = await hashPassword(password, salt);
  return timingSafeEqual(computedHash, storedHash);
}

/**
 * Validate password strength requirements
 * @returns null if valid, or an error message string
 */
export function validatePasswordStrength(password: string): string | null {
  if (password.length < 8) {
    return 'Password must be at least 8 characters';
  }
  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one number';
  }
  if (!/[^a-zA-Z0-9]/.test(password)) {
    return 'Password must contain at least one special character';
  }
  return null;
}

/**
 * Hash a password and generate a new salt
 * Convenience function for creating new passwords
 * @param password - The plain text password
 * @returns Object with salt and hash (both hex strings)
 */
export async function hashNewPassword(password: string): Promise<{ salt: string; hash: string }> {
  const salt = generateSalt();
  const hash = await hashPassword(password, salt);
  return { salt, hash };
}

// Helper: Convert Uint8Array to hex string
function arrayToHex(arr: Uint8Array): string {
  return Array.from(arr)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Helper: Convert hex string to Uint8Array
function hexToArray(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

// Helper: Timing-safe string comparison to prevent timing attacks
function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false;
  }
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}
